import React, { useState, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import { makeStyles } from '@material-ui/styles'
import { useSelector, useDispatch } from 'react-redux'
import { Input, Button, Picker } from '../root/components/common'
import { enqueueSnackbar, setLocale } from '../root/actions'
import DoneIcon from '@material-ui/icons/Done'
import { Fade, Box, Typography } from '@material-ui/core'
import { isEmailString } from '../../utils'
import * as actions from './actions'
import { LOCALE_DISPLAY_OPTIONS } from '../root/constants'
import { PasswordInput } from '../root/components/common/password-input/password-input'
import { useRef } from 'react'
import { usePasswordValidate } from '../root/components/common/password-input/use-password-validate'
import { useBooleanState } from 'web/src/utils/hooks/useBooleanState'
import Logo from 'web/src/assets/svg/logo.svg'
import EmailIcon from 'web/src/assets/svg/email-icon.svg'
import { contactEmail, copyrightText } from './constants'
import { InfoSection } from './partials/info-section'

const useStyles = makeStyles(theme => ({
    wrapper: {
        width: '100%',
        backgroundColor: theme.background,
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        padding: '0 10px',
        [theme.breakpoints.up('sm')]: {
            height: '100%'
        },
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            padding: '0'
        }
    },
    wrapperForm: {
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 20,
        paddingBottom: 20,
        [theme.breakpoints.up('sm')]: {
            width: '45vw'
        },
        [theme.breakpoints.down('xs')]: {
            paddingTop: 50
        }
    },
    input: {
        marginBottom: 15
    },
    actionWrapper: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 15
    },
    textWrapper: {
        marginBottom: 15,
        color: theme.primary
    },
    link: {
        fontWeight: 'bold',
        color: theme.primary,
        cursor: 'pointer'
    },
    relative: {
        position: 'relative',
        paddingRight: 20
    },
    doneIcon: {
        position: 'absolute',
        color: theme.active,
        top: 10,
        right: -5
    },
    langPickerWrapper: {
        position: 'absolute',
        top: 20,
        right: 20
    },

    logo: {
        zIndex: 999,
        [theme.breakpoints.down('xs')]: {
            position: 'absolute',
            top: 0,
            padding: 20
        }
    },
    formSection: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1
    },
    formSectionForm: {
        maxWidth: 600,
        padding: 15
    },
    formSectionHeading: {
        fontWeight: 700,
        fontSize: 33,
        color: theme.blueDark,
        [theme.breakpoints.down('md')]: {
            textAlign: 'center'
        }
    },
    formSectionNormalText: {
        fontSize: '17px',
        marginBottom: 40,
        maxWidth: 500,
        [theme.breakpoints.down('md')]: {
            textAlign: 'center'
        }
    },

    form: { display: 'flex', flexDirection: 'column', gap: 20 },

    registerButton: {
        marginTop: 30,
        background: theme.blueDark
    },

    formSectionFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            padding: '10px 5px',
            position: 'absolute',
            bottom: 0,
            zIndex: 999
        }
    },

    orSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 30px',
        fontSize: '30px',
        fontWeight: 700,
        color: theme.blueDark,
        [theme.breakpoints.down('md')]: {
            padding: '0 15px'
        }
    },
    formSectionFooterEmail: {
        display: 'flex',
        alignItems: 'center',
        gap: 5
    }
}))

function Register(props) {
    const classes = useStyles()
    const dispatch = useDispatch()
    const { userId, customerId } = useSelector(state => state.register)
    const { locale, localeCode } = useSelector(state => state.main)
    const passwordRef = useRef()
    const pristine = useBooleanState(true)
    // user vars
    const [userName, setUserName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [passwordProof, setPasswordProof] = useState('')
    const { validators, isPasswordValid } = usePasswordValidate(password)
    // customer vars
    const [customerName, setCustomerName] = useState('')

    const isNameOk = str => {
        return str.length > 2
    }

    const isPasswordOk = p => {
        return p.length > 6
    }

    const arePasswordsEqual = (p, pProof) => {
        return p.length > 0 && pProof.length > 0 && p === pProof
    }

    const goToLogin = () => {
        props.history.push('/login')
    }

    const validateUser = (name, email, p, pProof) => {
        let ok = true
        if (!isNameOk(name)) {
            ok = false
            dispatch(
                enqueueSnackbar({
                    message: locale.register.nameIsTooShort,
                    options: {
                        variant: 'warning'
                    }
                })
            )
        }
        if (!isEmailString(email)) {
            ok = false
            dispatch(
                enqueueSnackbar({
                    message: locale.register.emailIsNotCorrect,
                    options: {
                        variant: 'warning'
                    }
                })
            )
        }

        if (!arePasswordsEqual(p, pProof)) {
            ok = false
            dispatch(
                enqueueSnackbar({
                    message: locale.register.passwordsDoNotMatch,
                    options: {
                        variant: 'warning'
                    }
                })
            )
        }
        if (!isPasswordValid) {
            ok = false
            pristine.off()
            passwordRef.current.focus()
        }
        return ok
    }

    const validateCustomer = name => {
        let ok = true

        if (!isNameOk(name)) {
            ok = false
            dispatch(
                enqueueSnackbar({
                    message: locale.register.nameIsTooShort,
                    options: {
                        variant: 'warning'
                    }
                })
            )
        }
        return ok
    }

    const registerUser = (name, email, p, pProof) => {
        if (validateUser(name, email, p, pProof)) {
            dispatch(
                actions.userRegisterRequest({
                    name,
                    email,
                    password: p
                })
            )
        }
    }

    const registerCustomer = (name, user) => {
        if (validateCustomer(name)) {
            dispatch(
                actions.customerRegisterRequest({
                    name,
                    user: user
                })
            )
        }
    }

    const onLanguageSelect = localeCode => {
        dispatch(setLocale(localeCode))
    }

    useEffect(() => {
        return () => {
            dispatch(actions.userRegisterSuccess(null))
            dispatch(actions.customerRegisterSuccess(null))
        }
    }, [])

    return (
        <>
            <Box className={classes.wrapper}>
                <Box className={classes.wrapperForm}>
                    <Box className={classes.logo}>
                        <img src={Logo} alt="img" />
                    </Box>
                    <Box className={classes.langPickerWrapper}>
                        <Picker
                            onChange={onLanguageSelect}
                            value={localeCode}
                            options={LOCALE_DISPLAY_OPTIONS}
                        />
                    </Box>
                    <Box className={classes.formSection}>
                        {userId === null && customerId === null && (
                            <Fade
                                in={userId === null && customerId === null}
                                timeout={500}
                            >
                                <Box className={classes.formSectionForm}>
                                    <Typography
                                        variant="body1"
                                        className={classes.formSectionHeading}
                                    >
                                        {
                                            locale.register
                                                .registerYourOrganization
                                        }
                                    </Typography>
                                    <Typography
                                        className={
                                            classes.formSectionNormalText
                                        }
                                    >
                                        {locale.register.youAreAPestController}
                                    </Typography>
                                    <Box className={classes.form}>
                                        <Box className={classes.relative}>
                                            <Input
                                                autoFocus
                                                className={classes.input}
                                                label={locale.common.name}
                                                type="text"
                                                setValue={setUserName}
                                                onEnter={registerUser.bind(
                                                    this,
                                                    userName,
                                                    email,
                                                    password,
                                                    passwordProof
                                                )}
                                                value={userName}
                                                variant="standard"
                                            />
                                            <Fade
                                                in={isNameOk(userName)}
                                                timeout={500}
                                            >
                                                <DoneIcon
                                                    className={classes.doneIcon}
                                                />
                                            </Fade>
                                        </Box>

                                        <Box className={classes.relative}>
                                            <Input
                                                className={classes.input}
                                                label={locale.common.email}
                                                type="email"
                                                setValue={setEmail}
                                                onEnter={registerUser.bind(
                                                    this,
                                                    userName,
                                                    email,
                                                    password,
                                                    passwordProof
                                                )}
                                                value={email}
                                                variant="standard"
                                            />
                                            <Fade
                                                in={isEmailString(email)}
                                                timeout={500}
                                            >
                                                <DoneIcon
                                                    className={classes.doneIcon}
                                                />
                                            </Fade>
                                        </Box>

                                        <Box className={classes.relative}>
                                            <PasswordInput
                                                isError={
                                                    !pristine.is &&
                                                    !isPasswordValid
                                                }
                                                inputRef={passwordRef}
                                                validators={validators}
                                                className={classes.input}
                                                label={locale.common.password}
                                                type="password"
                                                setValue={setPassword}
                                                onEnter={registerUser.bind(
                                                    this,
                                                    userName,
                                                    email,
                                                    password,
                                                    passwordProof
                                                )}
                                                value={password}
                                                variant="standard"
                                            />

                                            <Fade
                                                in={isPasswordValid}
                                                timeout={500}
                                            >
                                                <DoneIcon
                                                    className={classes.doneIcon}
                                                />
                                            </Fade>
                                        </Box>
                                        <Box className={classes.relative}>
                                            <Input
                                                className={classes.input}
                                                label={
                                                    locale.common
                                                        .passwordConfirmation
                                                }
                                                type="password"
                                                setValue={setPasswordProof}
                                                onEnter={registerUser.bind(
                                                    this,
                                                    userName,
                                                    email,
                                                    password,
                                                    passwordProof
                                                )}
                                                value={passwordProof}
                                                variant="standard"
                                            />

                                            <Fade
                                                in={arePasswordsEqual(
                                                    password,
                                                    passwordProof
                                                )}
                                                timeout={500}
                                            >
                                                <DoneIcon
                                                    className={classes.doneIcon}
                                                />
                                            </Fade>
                                        </Box>

                                        <Button
                                            className={classes.registerButton}
                                            onClick={registerUser.bind(
                                                this,
                                                userName,
                                                email,
                                                password,
                                                passwordProof
                                            )}
                                        >
                                            {locale.register.registerOrg}
                                        </Button>

                                        <Typography
                                            variant="body2"
                                            style={{ textAlign: 'center' }}
                                        >
                                            {
                                                locale.register
                                                    .alreadyHaveAnAccount
                                            }
                                            <Typography
                                                onClick={goToLogin}
                                                variant="body2"
                                                component="span"
                                                style={{
                                                    fontWeight: 700,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {' '}
                                                {locale.register.login}
                                            </Typography>
                                        </Typography>
                                    </Box>
                                </Box>
                            </Fade>
                        )}

                        {userId !== null && customerId === null && (
                            <Fade
                                in={userId !== null && customerId === null}
                                timeout={500}
                            >
                                <div>
                                    <div className={classes.textWrapper}>
                                        {locale.register.pleaseEnterOrgName}
                                    </div>
                                    <div className={classes.relative}>
                                        <Input
                                            autoFocus
                                            className={classes.input}
                                            label={locale.register.orgName}
                                            type="text"
                                            setValue={setCustomerName}
                                            onEnter={registerCustomer.bind(
                                                this,
                                                customerName,
                                                {
                                                    id: userId,
                                                    name: userName,
                                                    email: email
                                                }
                                            )}
                                            value={customerName}
                                        />
                                        <Fade
                                            in={isNameOk(customerName)}
                                            timeout={500}
                                        >
                                            <DoneIcon
                                                className={classes.doneIcon}
                                            />
                                        </Fade>
                                    </div>
                                    <div className={classes.actionWrapper}>
                                        <Button
                                            onClick={registerCustomer.bind(
                                                this,
                                                customerName,
                                                {
                                                    id: userId,
                                                    name: userName,
                                                    email: email
                                                }
                                            )}
                                        >
                                            {locale.register.registerOrg}
                                        </Button>
                                    </div>
                                </div>
                            </Fade>
                        )}
                        {userId !== null && customerId !== null && (
                            <Fade
                                in={userId !== null && customerId !== null}
                                timeout={500}
                            >
                                <div>
                                    <div className={classes.actionWrapper}>
                                        {locale.register.everythingIsReady}
                                    </div>
                                    <div className={classes.actionWrapper}>
                                        <div
                                            onClick={goToLogin}
                                            className={classes.link}
                                        >
                                            {locale.register.goToLogin}
                                        </div>
                                    </div>
                                </div>
                            </Fade>
                        )}
                    </Box>

                    <Box className={classes.formSectionFooter}>
                        <Typography variant="body2">{copyrightText}</Typography>
                        <Typography
                            variant="body2"
                            className={classes.formSectionFooterEmail}
                        >
                            <img src={EmailIcon} alt="icon" /> {contactEmail}
                        </Typography>
                    </Box>
                </Box>
                <Typography className={classes.orSection}>
                    {locale.register.or}
                </Typography>
                <InfoSection />
            </Box>
        </>
    )
}

export default withRouter(Register)
